import { toast } from 'sonner';
import { useDomainStore } from '~/stores/domain-store'; // Import the hook
import {
  generateDomains,
  checkSpecificVariations,
  getDomainDescription, // Re-import getDomainDescription
} from './domain-service';
// SpecificDomainCheckResult removed - inferred type
import type { SearchSettings } from '~/config/search-settings'; // Use type import
import type { DomainSuggestion, DomainDescription } from '~/types/domain'; // Add DomainDescription
import type { Tld } from '~/config/tld-config'; // Import Tld type
// import pQueue from 'p-queue'; // Removed unused import

// --- Add ApiCallStats Interface ---
export interface ApiCallStats {
  generateDomains: number;
  checkSpecificVariations: number; // Track calls initiated by SearchService
  getDomainDescription: number; // Add stat for description fetches
  // Add other stats if needed (e.g., cache hits handled here)
}

// Note: SearchSettings type definition might be needed if not globally available
// import type { SearchSettings } from '~/config/search-settings';

export class SearchService {
  #currentAbort?: AbortController;
  // Use p-queue for batching API calls if checkSpecificVariations doesn't handle concurrency internally
  // If checkSpecificVariations *does* handle concurrency, this queue might be redundant.
  // Assuming checkSpecificVariations handles its own queue/concurrency for now.
  // #availabilityQueue = new pQueue({ concurrency: 5 });

  // --- Add private stats property ---
  #apiCallStats: ApiCallStats = {
    generateDomains: 0,
    checkSpecificVariations: 0,
    getDomainDescription: 0, // Initialize stat
  };

  // --- Add public getter for stats ---
  getApiCallStats(): ApiCallStats {
    return { ...this.#apiCallStats }; // Return a copy
  }

  async search(query: string, settings: SearchSettings): Promise<void> {
    // Cancel previous run
    if (this.#currentAbort) {
      this.#currentAbort.abort('New search initiated');
      // If checkSpecificVariations manages its own cancellation via signal,
      // clearing a local queue might not be necessary or sufficient.
      // Rely on passing the signal to checkSpecificVariations.
      // this.#availabilityQueue.clear();
    }
    this.#currentAbort = new AbortController();
    const { signal } = this.#currentAbort;

    const storeActions = useDomainStore.getState();
    storeActions.clearDomains(); // Reset results before new search

    try {
      storeActions.setIsGenerating(true); // Use renamed action
      storeActions.clearErrors(); // Clear previous errors

      // 1. Generate Suggestions
      // Construct DomainGenerationSettings for the API call
      const generationSettings = {
        query: query, // Add query from search parameters
        mode: settings.mode,
        maxLength: settings.maxLength,
        creativityLevel: settings.creativityLevel,
        domainLanguage: settings.domainLanguage,
        // Determine suggestTlds based on tldMode
        suggestTlds: settings.tldMode === 'auto',
        // Pass explicitTlds only if tldMode is 'explicit'
        explicitTlds:
          settings.tldMode === 'explicit' ? settings.selectedTlds : undefined,
        avoidExpensiveTlds: settings.avoidExpensiveTlds,
        // blacklist: undefined, // Add blacklist if needed later
      };

      // Note: generateDomains currently doesn't accept a signal.
      // Need to modify generateDomains later or handle cancellation differently.
      this.#apiCallStats.generateDomains++; // Increment stat
      const { suggestions } = await generateDomains(
        generationSettings /*, { signal } */
      );
      // TODO: Add signal check here once generateDomains supports it.
      // if (signal.aborted) return;

      storeActions.upsertDomains(suggestions); // Upsert suggestions into store

      // --- Determine and store the TLDs used for this search ---
      let searchTlds: Tld[];
      if (settings.tldMode === 'explicit') {
        searchTlds = settings.selectedTlds;
      } else {
        // Extract unique TLDs from the generated suggestions
        searchTlds = this.#extractTldsFromSuggestions(suggestions);
      }
      storeActions.setSearchTlds(searchTlds);
      // --- End TLD storing ---

      storeActions.setIsGenerating(false); // Use renamed action

      // 2. Filter uncached variations for availability checks
      const uncachedVariationNames =
        this.#getUncachedVariationNames(suggestions);
      // console.log('[SearchService] Uncached variations found:', uncachedVariationNames); // Removed log
      if (!uncachedVariationNames.length) {
        // console.log('[SearchService] All variations found in cache. Skipping availability check.'); // Removed log
        storeActions.setIsCheckingAvailability(false); // Use renamed action
        return;
      }

      // 3. Check Availability Batch
      storeActions.setIsCheckingAvailability(true); // Use renamed action
      // Pass the signal to checkSpecificVariations for cancellation
      this.#apiCallStats.checkSpecificVariations++; // Increment stat
      // console.log('[SearchService] Proceeding to call #checkAvailabilityBatch...'); // Removed log
      await this.#checkAvailabilityBatch(uncachedVariationNames, signal);
    } catch (error: any) {
      if (error.name === 'AbortError' || signal.aborted) {
        console.log('Search aborted:', error?.message);
        // Do not show AbortError to the user as per requirements
      } else {
        console.error('Search failed:', error);
        storeActions.setApiError(
          // Use renamed action
          error.message || 'An unknown error occurred during search.'
        );
        // Potentially trigger toast notification here
      }
    } finally {
      // Ensure loading states are reset if the search completes or errors out (except for abort)
      if (!signal.aborted) {
        storeActions.setIsGenerating(false); // Use renamed action
        storeActions.setIsCheckingAvailability(false); // Use renamed action
      }
      // Clean up abort controller reference if this search is finished
      if (this.#currentAbort?.signal === signal) {
        this.#currentAbort = undefined;
      }
    }
  }

  /**
   * Filters domain suggestions to find variation names whose availability is not cached.
   */
  #getUncachedVariationNames(suggestions: DomainSuggestion[]): string[] {
    const cache = useDomainStore.getState().availabilityCache;
    const uncachedNames: string[] = [];

    suggestions.forEach((suggestion) => {
      suggestion.variations.forEach((variation) => {
        if (cache[variation.name] === undefined) {
          uncachedNames.push(variation.name);
        }
        // Note: The store's upsertDomains now handles applying cached status,
        // so no need to call patchAvailability here.
      });
    });
    // console.log(`Found ${uncachedNames.length} uncached variations.`); // Removed log
    return uncachedNames;
  }

  /**
   * Extracts unique TLDs from a list of domain suggestions.
   */
  #extractTldsFromSuggestions(suggestions: DomainSuggestion[]): Tld[] {
    const tldSet = new Set<Tld>();
    const getBaseName = useDomainStore.getState()._getBaseName; // Use store's helper

    suggestions.forEach((suggestion) => {
      suggestion.variations.forEach((variation) => {
        const baseName = getBaseName(variation.name);
        if (baseName !== null) {
          const tld = variation.name.substring(baseName.length) as Tld;
          // Basic validation: starts with '.' and doesn't contain another '.'
          if (tld.startsWith('.') && !tld.substring(1).includes('.')) {
            tldSet.add(tld);
          }
        }
      });
    });
    return Array.from(tldSet);
  }

  /**
   * Checks availability for a batch of variation names.
   * Uses checkSpecificVariations which should handle concurrency and signals.
   * Implements early-exit logic based on results.
   */
  async #checkAvailabilityBatch(
    variationNames: string[],
    signal: AbortSignal
  ): Promise<void> {
    const storeActions = useDomainStore.getState();
    const checkedBaseNames = new Set<string>(); // Track base names with an available variation found

    // We need base names for early exit. Extract them.
    // This relies on the store's _getBaseName helper.
    const getBaseName = storeActions._getBaseName;
    const variationToBaseNameMap = new Map<string, string | null>();
    variationNames.forEach((name) => {
      variationToBaseNameMap.set(name, getBaseName(name));
    });

    // Filter names for the actual check, respecting early exit for already found base names
    const namesToCheck = variationNames.filter((name) => {
      const baseName = variationToBaseNameMap.get(name);
      return baseName ? !checkedBaseNames.has(baseName) : true; // Check if baseName unknown or not yet found
    });

    if (!namesToCheck.length) {
      // console.log('No variations left to check after initial early-exit filter.'); // Removed log
      storeActions.setIsCheckingAvailability(false); // Use renamed action
      return;
    }

    try {
      // Call the domain service function - it should handle batching/concurrency
      // Pass the signal for cancellation.
      // console.log(`[#checkAvailabilityBatch] Calling checkSpecificVariations for ${namesToCheck.length} names:`, namesToCheck); // Removed log
      const results = await checkSpecificVariations(
        namesToCheck /*, { signal } */
      ); // TODO: Pass signal when supported by checkSpecificVariations

      if (signal.aborted) return; // Check if aborted during the batch call

      // Process results and update store
      const processedNames = new Set<string>(); // Track names processed from results
      results.forEach((result) => {
        processedNames.add(result.name); // Add name to processed set
        if (signal.aborted) return; // Check frequently during processing

        // Update store with the full result (success or error)
        // patchAvailability now handles the different result states
        storeActions.patchAvailability(result.name, result);

        // Early exit logic: if available, mark base name as checked
        // This logic remains, but the store update happens regardless
        if (result.available === true) {
          // Check specifically for true
          const baseName = variationToBaseNameMap.get(result.name);
          if (baseName) {
            checkedBaseNames.add(baseName);
            // TODO: Potentially cancel *pending* checks for the same baseName
            // This requires checkSpecificVariations to support finer-grained cancellation
            // or modifications here if using p-queue.
          }
        }
        // No need for the separate 'else if (result.error)' block,
        // as patchAvailability(result.name, result) handles error cases now.
      });

      // Log names that were requested but not found in the results
      const missingNames = namesToCheck.filter(
        (name) => !processedNames.has(name)
      );
      if (missingNames.length > 0) {
        console.warn(
          `[#checkAvailabilityBatch] Did not receive results for ${missingNames.length} requested names:`,
          missingNames
        );
      }
    } catch (error: any) {
      if (error.name !== 'AbortError' && !signal.aborted) {
        console.error('Failed during checkAvailabilityBatch:', error);
        storeActions.setApiError(
          // Use renamed action
          error.message || 'Failed to check availability.'
        );
      } else {
        // console.log('Availability check aborted.'); // Removed log
      }
    } finally {
      if (!signal.aborted) {
        storeActions.setIsCheckingAvailability(false); // Use renamed action
      }
    }
  }

  /**
   * Generates additional domain suggestions based on the current query, settings,
   * and the TLDs used in the initial search. Does not suggest new TLDs.
   * @param query The current search query.
   * @param settings The current search settings.
   */
  async generateMore(query: string, settings: SearchSettings): Promise<void> {
    const storeState = useDomainStore.getState(); // Get current store state
    const storeActions = useDomainStore.getState(); // Get actions

    // Prevent concurrent generation or if max limit reached
    if (storeState.isGenerating || storeState.isCheckingAvailability) {
      console.warn(
        '[generateMore] Generation or check already in progress. Skipping.'
      );
      return;
    }
    // Check against MAX_DOMAINS_TO_LOAD (Import needed)
    // Assuming MAX_DOMAINS_TO_LOAD is available or we handle it in UI
    // Let's add a check here for safety, though the UI also checks
    // import { MAX_DOMAINS_TO_LOAD } from '~/config/search-settings'; // Needs import at top
    // if (storeState.ids.length >= MAX_DOMAINS_TO_LOAD) {
    //   console.warn('[generateMore] Maximum domain limit reached. Skipping.');
    //   return;
    // }

    // Use existing abort controller logic
    if (this.#currentAbort) {
      this.#currentAbort.abort('New generation (more) initiated');
    }
    this.#currentAbort = new AbortController();
    const { signal } = this.#currentAbort;

    try {
      storeActions.setIsGenerating(true);
      storeActions.clearErrors();

      // Get TLDs from the store
      const tldsToUse = storeState.searchTlds;
      if (!tldsToUse || tldsToUse.length === 0) {
        console.warn(
          '[generateMore] No search TLDs found in store. Cannot generate more.'
        );
        storeActions.setApiError(
          'Cannot generate more domains without initial search TLDs.'
        );
        storeActions.setIsGenerating(false);
        return;
      }

      // Construct generation settings for "more"
      const generationSettings = {
        query: query, // Use passed query
        mode: settings.mode, // Use passed settings
        maxLength: settings.maxLength,
        creativityLevel: settings.creativityLevel,
        domainLanguage: settings.domainLanguage,
        suggestTlds: false, // Explicitly disable TLD suggestion
        explicitTlds: tldsToUse, // Use TLDs from the store
        avoidExpensiveTlds: settings.avoidExpensiveTlds,
        // TODO: Add offset/exclude parameter if API supports it
      };

      this.#apiCallStats.generateDomains++; // Increment stat
      // console.log('[SearchService] Calling generateDomains for generateMore...'); // Removed log
      const { suggestions: newSuggestions } = await generateDomains(
        generationSettings /*, { signal } */ // Pass signal when supported
      );

      if (signal.aborted) return;

      // Filter out suggestions whose baseName already exists
      const existingBaseNames = new Set(storeState.ids);
      const uniqueNewSuggestions = newSuggestions.filter(
        (s) => !existingBaseNames.has(s.baseName)
      );

      if (uniqueNewSuggestions.length === 0) {
        // console.log('[SearchService] generateMore returned no new unique suggestions.'); // Removed log
        storeActions.setIsGenerating(false);
        // Optionally set a specific message in the store or UI
        return;
      }

      storeActions.upsertDomains(uniqueNewSuggestions); // Add only new ones

      storeActions.setIsGenerating(false); // Done generating

      // Check availability for the *new* variations
      const uncachedVariationNames =
        this.#getUncachedVariationNames(uniqueNewSuggestions);
      if (!uncachedVariationNames.length) {
        // console.log('[SearchService] All new variations from generateMore found in cache.'); // Removed log
        storeActions.setIsCheckingAvailability(false); // Ensure this is false if no check needed
        return;
      }

      storeActions.setIsCheckingAvailability(true); // Start checking
      this.#apiCallStats.checkSpecificVariations++;
      // console.log('[SearchService] Calling #checkAvailabilityBatch for new variations...'); // Removed log
      await this.#checkAvailabilityBatch(uncachedVariationNames, signal);
    } catch (error: any) {
      if (error.name === 'AbortError' || signal.aborted) {
        console.log('[generateMore] Aborted:', error?.message);
      } else {
        console.error('[generateMore] Failed:', error);
        // Use toast instead of setApiError for generateMore failures
        toast.error(
          error.message ||
            'An unknown error occurred while generating more domains.'
        );
      }
    } finally {
      // Ensure loading states are reset unless aborted
      if (!signal.aborted) {
        storeActions.setIsGenerating(false);
        storeActions.setIsCheckingAvailability(false);
      }
      // Clean up abort controller reference
      if (this.#currentAbort?.signal === signal) {
        this.#currentAbort = undefined;
      }
    }
  }

  /**
   * Fetches the AI-generated description for a specific domain name.
   * @param domainName The domain name to get the description for.
   * @param language The language code for the description.
   * @param userQuery The original user query that led to this domain.
   * @returns A promise resolving to the DomainDescription.
   */
  async fetchDomainDescription(
    domainName: string,
    language: string,
    userQuery: string
  ): Promise<DomainDescription> {
    // Note: Cancellation is not currently supported by the underlying getDomainDescription.
    // Consider adding if the service function is updated.
    // const signal = this.#currentAbort?.signal; // Potentially use a shared signal? Or a dedicated one?

    this.#apiCallStats.getDomainDescription++; // Increment stat
    // console.log(`[SearchService] Fetching description for ${domainName} (lang: ${language})`); // Removed log

    try {
      // Call the original service function
      const description = await getDomainDescription(
        domainName,
        language,
        userQuery
        // { signal } // Pass signal if supported
      );
      // if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      return description;
    } catch (error: any) {
      console.error(
        `[SearchService] Failed to fetch description for ${domainName}:`,
        error
      );
      // Re-throw the error to be handled by the caller (e.g., the UI hook)
      throw error;
    }
  }

  cancelCurrentSearch(): void {
    if (this.#currentAbort) {
      this.#currentAbort.abort('User cancelled search');
      // No local queue to clear, rely on signal propagation
      // console.log('Current search cancelled by user.'); // Removed log
      // Reset loading states immediately on user cancel
      const storeActions = useDomainStore.getState();
      storeActions.setIsGenerating(false); // Use renamed action
      storeActions.setIsCheckingAvailability(false); // Use renamed action
      this.#currentAbort = undefined;
    }
  }
}

// Export a singleton instance
export const searchService = new SearchService();
